import React, { useState, useEffect } from 'react';
import { Menu, X, Download, Ghost, Brain, Shuffle, Zap, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const Home = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  return (
    <div className="escape-dark-room">
      {/* Header */}
      <header className={`header ${scrolled ? 'scrolled' : ''}`}>
        <div className="header-content">
          <div className="header-title">
            ESCAPE THE DARK ROOM
          </div>

          {/* Desktop Navigation */}
          <nav className="desktop-nav">
            <button onClick={() => scrollToSection('hero')} className="nav-link">Beranda</button>
            <button onClick={() => scrollToSection('story')} className="nav-link">Cerita</button>
            <button onClick={() => scrollToSection('features')} className="nav-link">Fitur</button>
            <button onClick={() => scrollToSection('gallery')} className="nav-link">Galeri</button>
            <button onClick={() => scrollToSection('team')} className="nav-link">Tim</button>
            <button onClick={() => scrollToSection('download')} className="nav-link">Download</button>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className="mobile-menu-btn"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="mobile-nav">
            <button onClick={() => scrollToSection('hero')} className="mobile-nav-link">Beranda</button>
            <button onClick={() => scrollToSection('story')} className="mobile-nav-link">Cerita</button>
            <button onClick={() => scrollToSection('features')} className="mobile-nav-link">Fitur</button>
            <button onClick={() => scrollToSection('gallery')} className="mobile-nav-link">Galeri</button>
            <button onClick={() => scrollToSection('team')} className="mobile-nav-link">Tim</button>
            <button onClick={() => scrollToSection('download')} className="mobile-nav-link">Download</button>
          </nav>
        )}
      </header>

      {/* Hero Section */}
      <section id="hero" className="hero-section">
        <div className="hero-content">
          <div className="hero-logo-wrapper">
            <img 
              src="https://customer-assets.emergentagent.com/job_80a2eb1f-77fd-480a-bde1-1f1ab4841f74/artifacts/pmm5dh75_Gemini_Generated_Image_rjiu88rjiu88rjiu-removebg-preview.png" 
              alt="Escape The Dark Room" 
              className="hero-logo"
            />
          </div>
          <p className="hero-subtitle">
            Sebuah gedung kampus terbengkalai selama 10 tahun menyimpan rahasia kelam tentang 'Kelas Malam' yang memakan korban. 
            Kamu masuk untuk mencari jawaban, namun kini... kamu yang menjadi targetnya.
          </p>
          <p className="hero-tagline">Bisakah kamu menjaga kewarasanmu dan keluar hidup-hidup sebelum terlambat?</p>
          <div className="hero-cta">
            <Button 
              onClick={() => scrollToSection('download')} 
              className="btn-primary-horror"
            >
              <Download size={20} />
              Download Game
            </Button>
            <Button 
              onClick={() => scrollToSection('story')} 
              className="btn-secondary-horror"
            >
              Pelajari Lebih Lanjut
              <ChevronDown size={20} />
            </Button>
          </div>
        </div>
        <div className="hero-overlay"></div>
      </section>

      {/* Story Section */}
      <section id="story" className="story-section">
        <div className="container">
          <h2 className="section-title">Cerita Game</h2>
          <div className="section-divider"></div>
          
          <Accordion type="single" collapsible className="story-accordion">
            <AccordionItem value="item-1" className="accordion-item-horror">
              <AccordionTrigger className="accordion-trigger-horror">
                <span className="accordion-number">01</span>
                Legenda Kelas Malam
              </AccordionTrigger>
              <AccordionContent className="accordion-content-horror">
                <p>
                  Gedung Fakultas lama ini sudah dikunci rapat selama satu dekade. Rumor yang beredar di kalangan mahasiswa 
                  menyebutkan tentang tragedi "Kelas Malam". Konon, sepuluh tahun lalu, sekumpulan mahasiswa terpilih 
                  diundang untuk mengikuti ujian rahasia di tengah malam. Namun, anehnya... tak ada satu pun dari mereka 
                  yang pernah terlihat lagi setelah malam itu. Mereka lenyap tanpa jejak, meninggalkan gedung itu kosong dan dingin.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="accordion-item-horror">
              <AccordionTrigger className="accordion-trigger-horror">
                <span className="accordion-number">02</span>
                Rasa Penasaran yang Membawa Petaka
              </AccordionTrigger>
              <AccordionContent className="accordion-content-horror">
                <p>
                  Kamu bermain sebagai seorang mahasiswa yang skeptis namun penasaran. Berbekal sebuah senter dengan baterai 
                  terbatas, kamu nekat menyelinap masuk ke dalam gedung tersebut untuk membuktikan bahwa rumor itu hanyalah 
                  isapan jempol belaka. Namun, begitu pintu utama terkunci di belakangmu, kamu sadar bahwa kamu tidak sendirian.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="accordion-item-horror">
              <AccordionTrigger className="accordion-trigger-horror">
                <span className="accordion-number">03</span>
                Teror Psikologis
              </AccordionTrigger>
              <AccordionContent className="accordion-content-horror">
                <p>
                  Di dalam kegelapan, kamu menemukan bahwa gedung ini tidak benar-benar kosong. Arwah-arwah penasaran dari 
                  para mahasiswa yang hilang (The Lost Students) kini bergentayangan, menjaga setiap lorong dan ruangan. 
                  Setiap kali mereka mendekat, detak jantungmu berpacu kencang. Stress System dalam tubuhmu mulai bereaksi; 
                  pandanganmu memburam, napasmu memburu. Jika tingkat stresmu mencapai batasnya, kamu akan pingsan dan 
                  bernasib sama dengan mereka.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="accordion-item-horror">
              <AccordionTrigger className="accordion-trigger-horror">
                <span className="accordion-number">04</span>
                Teka-Teki Kelangsungan Hidup
              </AccordionTrigger>
              <AccordionContent className="accordion-content-horror">
                <p>Untuk keluar, berlari saja tidak cukup. Kamu harus memutar otak:</p>
                <ul className="story-list">
                  <li><strong>Investigasi:</strong> Kamu harus mencari dokumen dan Notes yang ditinggalkan korban sebelumnya.</li>
                  <li><strong>Eksplorasi:</strong> Mencari kunci ruangan vital yang tersebar secara acak (Ruang Dosen, Ruang Rapat, dan Kelas).</li>
                  <li><strong>Deduksi:</strong> Menemukan dan memecahkan Kode Brankas yang selalu berubah setiap malam, yang menyimpan kunci kebebasanmu.</li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="accordion-item-horror">
              <AccordionTrigger className="accordion-trigger-horror">
                <span className="accordion-number">05</span>
                Jalan Keluar
              </AccordionTrigger>
              <AccordionContent className="accordion-content-horror">
                <p>
                  Waktu terus berjalan. Senter semakin redup. Hantu-hantu semakin agresif. Satu-satunya tujuanmu adalah 
                  menemukan kunci pintu utama dan Escape The Dark Room sebelum kamu menjadi bagian dari legenda "Kelas Malam" 
                  selamanya.
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="container">
          <h2 className="section-title">Fitur Utama</h2>
          <div className="section-divider"></div>
          
          <div className="features-grid">
            <Card className="feature-card">
              <CardContent className="feature-content">
                <div className="feature-icon">
                  <Ghost size={48} />
                </div>
                <h3 className="feature-title">Dynamic Jumpscare</h3>
                <p className="feature-description">
                  Musuh akan mengejarmu jika kamu terlalu berisik atau terlihat. Setiap pergerakan harus diperhitungkan 
                  dengan hati-hati untuk menghindari konfrontasi dengan arwah penasaran.
                </p>
              </CardContent>
            </Card>

            <Card className="feature-card">
              <CardContent className="feature-content">
                <div className="feature-icon">
                  <Brain size={48} />
                </div>
                <h3 className="feature-title">Immersive Stress System</h3>
                <p className="feature-description">
                  Layar akan berubah dan detak jantung terdengar semakin kencang saat hantu mendekat. 
                  Jaga kewarasanmu atau kamu akan kehilangan kendali!
                </p>
              </CardContent>
            </Card>

            <Card className="feature-card">
              <CardContent className="feature-content">
                <div className="feature-icon">
                  <Shuffle size={48} />
                </div>
                <h3 className="feature-title">Replayable Puzzle</h3>
                <p className="feature-description">
                  Kode brankas dan lokasi item kunci (Key & Card) akan selalu teracak dan berpindah tempat setiap kali 
                  kamu memulai permainan baru. Rogue-like element yang membuat setiap playthrough unik!
                </p>
              </CardContent>
            </Card>

            <Card className="feature-card">
              <CardContent className="feature-content">
                <div className="feature-icon">
                  <Zap size={48} />
                </div>
                <h3 className="feature-title">Realistic Flashlight</h3>
                <p className="feature-description">
                  Sistem pencahayaan terbatas yang menambah atmosfer horor. Baterai sentermu terbatas, gunakan dengan bijak 
                  atau kamu akan tersesat dalam kegelapan total.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="gallery-section">
        <div className="container">
          <h2 className="section-title">Galeri Pengembangan</h2>
          <div className="section-divider"></div>
          
          <div className="gallery-grid">
            <div className="gallery-item">
              <img 
                src="https://customer-assets.emergentagent.com/job_80a2eb1f-77fd-480a-bde1-1f1ab4841f74/artifacts/f81xzlm8_unity%20view.png" 
                alt="Unity Development Process" 
                className="gallery-image"
              />
              <div className="gallery-overlay">
                <p className="gallery-caption">Proses Pengembangan di Unity Editor</p>
              </div>
            </div>

            <div className="gallery-item">
              <img 
                src="https://customer-assets.emergentagent.com/job_80a2eb1f-77fd-480a-bde1-1f1ab4841f74/artifacts/kfonvymx_image.png" 
                alt="Gameplay Screenshot" 
                className="gallery-image"
              />
              <div className="gallery-overlay">
                <p className="gallery-caption">Tampilan In-Game: Suasana Mencekam</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" className="team-section">
        <div className="container">
          <h2 className="section-title">Tim Pengembang</h2>
          <div className="section-divider"></div>
          
          <div className="team-grid">
            <Card className="team-card">
              <CardContent className="team-content">
                <div className="team-role">Lead Programmer & Game Design</div>
                <h3 className="team-name">Faiz Zain</h3>
              </CardContent>
            </Card>

            <Card className="team-card">
              <CardContent className="team-content">
                <div className="team-role">ENVIRONMENT & LEVEL ARCHITECTURE</div>
                <h3 className="team-name">Totok Kurniawan</h3>
                <h3 className="team-name">Miftah Farid</h3>
              </CardContent>
            </Card>

            <Card className="team-card">
              <CardContent className="team-content">
                <div className="team-role">NARRATIVE DESIGN & GAME ASSETS</div>
                <h3 className="team-name">Gadis Michellia</h3>
                <h3 className="team-name">Novita Yunda</h3>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Download Section */}
      <section id="download" className="download-section">
        <div className="container">
          <h2 className="section-title">Download Game</h2>
          <div className="section-divider"></div>
          
          <div className="download-content">
            <Card className="download-card">
              <CardContent className="download-card-content">
                <h3 className="download-title">ESCAPE THE DARK ROOM</h3>
                <div className="download-info">
                  <div className="info-row">
                    <span className="info-label">Platform:</span>
                    <span className="info-value">Windows & Mac/Linux</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Ukuran:</span>
                    <span className="info-value">131 MB</span>
                  </div>
                </div>

                <div className="specs-section">
                  <h4 className="specs-title">Spesifikasi Minimum</h4>
                  <ul className="specs-list">
                    <li><strong>OS:</strong> Windows 10 / macOS 10.13+</li>
                    <li><strong>Processor:</strong> Intel Core i3 / AMD equivalent</li>
                    <li><strong>Memory:</strong> 4 GB RAM</li>
                    <li><strong>Graphics:</strong> NVIDIA GTX 660 / AMD Radeon HD 7850</li>
                    <li><strong>Storage:</strong> 500 MB ruang tersedia</li>
                    <li><strong>DirectX:</strong> Version 11</li>
                  </ul>
                </div>

                <a 
                  href="https://drive.google.com/file/d/1GIK3Lrqgo0CMFZ2Qq6w_T7bT6ri3UE4m/view" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="download-link"
                >
                  <Button className="btn-download-horror">
                    <Download size={24} />
                    Download Sekarang
                  </Button>
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <h3 className="footer-title">ESCAPE THE DARK ROOM</h3>
            <p className="footer-text">
              © 2025 Escape The Dark Room. All Rights Reserved.
            </p>
            <p className="footer-warning">
              Berani Coba?
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;